import type { ReactNode } from 'react'
import { create } from 'zustand'

export interface DetailSidebarEntry {
  /** Stable id of the previewed record; lets openers de-dupe re-opens. */
  id?: string
  header?: ReactNode
  content: ReactNode
  onExpand?: () => void
  /** Run when the panel is closed by the user (close button / escape). */
  onClose?: () => void
}

interface DetailSidebarState {
  entry: DetailSidebarEntry | null
  open: (entry: DetailSidebarEntry) => void
  /** User-initiated close — runs the entry's `onClose` (e.g. clears the URL). */
  close: () => void
  /** Silent close used when the route changes — does not run `onClose`. */
  dismiss: () => void
  /** Silent close scoped to one record: a no-op unless `id` is still the open
   *  entry. Lets an owner clean up after itself without ever closing an entry
   *  someone else opened in the meantime. */
  dismissEntry: (id: string) => void
}

export const useDetailSidebarStore = create<DetailSidebarState>()(
  (set, get) => ({
    entry: null,
    open: (entry) => set({ entry }),
    close: () => {
      get().entry?.onClose?.()
      set({ entry: null })
    },
    dismiss: () => set({ entry: null }),
    dismissEntry: (id) => {
      if (get().entry?.id === id) set({ entry: null })
    },
  }),
)

export function useDetailSidebar() {
  const open = useDetailSidebarStore((state) => state.open)
  const close = useDetailSidebarStore((state) => state.close)
  const dismiss = useDetailSidebarStore((state) => state.dismiss)
  const dismissEntry = useDetailSidebarStore((state) => state.dismissEntry)
  const isOpen = useDetailSidebarStore((state) => state.entry !== null)
  return { open, close, dismiss, dismissEntry, isOpen }
}
